import React, { useState, useEffect } from 'react';

const generateTrailItems = (type) => {
  const items = [];
  const total = type === 'A' ? 15 : 14;

  const radius = 5; // minimum distance between circles (as % of screen)
  const buffer = 7; // spacing buffer
  const maxAttempts = 1000;

  const isTooClose = (x, y) => {
    return items.some((item) => {
      const dx = x - item.x;
      const dy = y - item.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      return dist < radius + buffer;
    });
  };

  for (let i = 0; i < total; i++) {
    let x, y, attempts = 0;

    do {
      x = Math.random() * 80 + 10; // percent
      y = Math.random() * 60 + 20;
      attempts++;
    } while (isTooClose(x, y) && attempts < maxAttempts);

    const value =
      type === 'A'
        ? i + 1
        : i % 2 === 0
        ? Math.floor(i / 2) + 1
        : String.fromCharCode(65 + Math.floor(i / 2));

    items.push({
      id: i,
      label: `${value}`,
      value,
      x,
      y,
      status: 'active',
    });
  }

  return items;
};

export default function TrailMaking({ onComplete }) {
  const [phase, setPhase] = useState('instructions');
  const [trailType, setTrailType] = useState('A');
  const [items, setItems] = useState([]);
  const [startTime, setStartTime] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [errors, setErrors] = useState(0);
  const [resultLog, setResultLog] = useState([]);

  useEffect(() => {
    if (phase === 'task') {
      const generated = generateTrailItems(trailType);
      setItems(generated);
      setStartTime(Date.now());
    }
  }, [phase, trailType]);

  const expectedValue = () => items[currentIndex]?.value;

  const handleClick = (item) => {
    if (item.value === expectedValue()) {
      updateItemStatus(item.id, 'correct');
      setCurrentIndex((i) => i + 1);

      if (currentIndex + 1 === items.length) {
        const duration = Date.now() - startTime;
        const data = {
          part: trailType,
          duration,
          errors,
        };

        setResultLog((prev) => [...prev, data]);

        if (trailType === 'A') {
          setTimeout(() => {
            setTrailType('B');
            setCurrentIndex(0);
            setErrors(0);
            setPhase('task');
          }, 1000);
        } else {
          if (onComplete) onComplete([...resultLog, data]);
          setPhase('done');
        }
      }
    } else {
      setErrors((e) => e + 1);
      updateItemStatus(item.id, 'incorrect');
      setTimeout(() => updateItemStatus(item.id, 'active'), 400);
    }
  };

  const updateItemStatus = (id, status) => {
    setItems((prev) =>
      prev.map((item) => (item.id === id ? { ...item, status } : item))
    );
  };

  if (phase === 'done') {
    return (
      <div style={{ textAlign: 'center', marginTop: '3rem' }}>
        <h2>Trail Making Test Complete</h2>
        {resultLog.map((part, i) => (
          <div key={i}>
            <h3>Part {part.part}</h3>
            <p>Time: {Math.round(part.duration / 1000)} seconds</p>
            <p>Errors: {part.errors}</p>
          </div>
        ))}
      </div>
    );
  }

  if (phase === 'instructions') {
    return (
      <div style={{ textAlign: 'center', marginTop: '3rem' }}>
        <h2>Welcome to the Trail Making Test</h2>
        <p><strong>Part A:</strong> Click the numbers in order (1 → 2 → 3 → ...).</p>
        <p><strong>Part B:</strong> Click alternating number–letter order (1 → A → 2 → B → ... up to 7 → G).</p>
        <p>Click as quickly and accurately as possible. You won’t be told what to click next.</p>
        <button onClick={() => setPhase('task')}>Start Part A</button>
      </div>
    );
  }

  return (
    <div style={{ position: 'relative', width: '100%', height: '80vh', padding: '1rem' }}>
      <h2 style={{ textAlign: 'center' }}>Trail Making Part {trailType}</h2>
      {items.map((item) => (
        <div
          key={item.id}
          onClick={() => handleClick(item)}
          style={{
            position: 'absolute',
            left: `${item.x}%`,
            top: `${item.y}%`,
            transform: 'translate(-50%, -50%)',
            backgroundColor:
              item.status === 'correct'
                ? 'lightgray'
                : item.status === 'incorrect'
                ? '#ff4d4d'
                : 'lightblue',
            borderRadius: '50%',
            width: '60px',
            height: '60px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '1rem',
            cursor: item.status === 'correct' ? 'default' : 'pointer',
            border: '1px solid gray',
            opacity: item.status === 'correct' ? 0.5 : 1,
            transition: 'background-color 0.2s, opacity 0.2s',
            zIndex: 1,
          }}
        >
          {item.label}
        </div>
      ))}
    </div>
  );
}
